/* CalPerc quote list — browser-side "Add to Quote" that feeds the
   rental quote form. Items live in localStorage; the quote page reads
   them into the instruments field so submissions name exact gear. */
(() => {
  const KEY = 'cpla_quote_v1';

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  };
  const write = (items) => {
    localStorage.setItem(KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('cpla:quote', { detail: { items } }));
  };

  window.CplaQuote = {
    items: read,
    count: () => read().length,
    add(item) {
      const items = read();
      const qty = Math.max(1, parseInt(item.qty, 10) || 1);
      const existing = items.find((i) => i.handle === item.handle);
      if (existing) {
        existing.qty = qty;
        if (item.sizes) existing.sizes = item.sizes;
      } else {
        items.push({ ...item, qty });
      }
      write(items);
      return items;
    },
    setSizes(handle, sizes) {
      const items = read();
      const item = items.find((i) => i.handle === handle);
      if (!item) return false;
      item.sizes = sizes;
      write(items);
      return true;
    },
    setQty(handle, qty) {
      const items = read();
      const item = items.find((i) => i.handle === handle);
      if (!item) return;
      const ceiling = parseInt(item.maxQty, 10) || 999;
      item.qty = Math.min(ceiling, Math.max(1, parseInt(qty, 10) || 1));
      write(items);
    },
    remove(handle) {
      write(read().filter((i) => i.handle !== handle));
    },
    clear() {
      write([]);
    },
  };

  // --- Header pill: show count, hide at zero ---
  const pills = () => document.querySelectorAll('[data-quote-pill]');
  const renderPills = () => {
    const n = window.CplaQuote.count();
    pills().forEach((el) => {
      el.hidden = n === 0;
      const badge = el.querySelector('[data-quote-count]');
      if (badge) badge.textContent = n;
    });
    // The Quote pill replaces the Reserve button once the list has items —
    // both lead to the same place, so only one shows at a time.
    document.querySelectorAll('[data-reserve-cta]').forEach((el) => {
      el.hidden = n > 0;
    });
  };

  // --- Drum size pills: pick the exact shells, they ride along with the kit ---
  // A pill belongs to whichever card it sits in; on a product page there is only
  // one product, so the scope is the document.
  const scopeOf = (el) => el.closest('.cpla-invcard') || document;
  const sizesIn = (scope) =>
    [...scope.querySelectorAll('.cp-sizes__sz[aria-pressed="true"]')].map((b) => {
      const grp = b.closest('.cp-sizes__grp');
      const label = grp && grp.querySelector('.cp-sizes__label');
      // "Rack Toms" -> "Rack Tom 10×7"
      const kind = label ? label.textContent.trim().replace(/s$/, '') : '';
      return (kind ? kind + ' ' : '') + b.dataset.cpSize;
    });

  const bindSizePills = () => {
    document.querySelectorAll('.cp-sizes__sz[data-cp-size]').forEach((btn) => {
      if (!btn.hasAttribute('aria-pressed')) btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', () => {
        const on = btn.getAttribute('aria-pressed') === 'true';
        btn.setAttribute('aria-pressed', on ? 'false' : 'true');
        // If this product is already on the quote, keep it in sync as they pick.
        const scope = scopeOf(btn);
        const add = scope.querySelector
          ? scope.querySelector('[data-quote-add]')
          : null;
        if (add) window.CplaQuote.setSizes(add.dataset.handle, sizesIn(scope));
      });
    });
  };

  // --- Product page: Add to Quote button ---
  const bindAddButtons = () => {
    document.querySelectorAll('[data-quote-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const qtyInput = document.querySelector('[data-quote-qty]');
        const qty = qtyInput ? clampQty(qtyInput) : 1;
        const sizes = sizesIn(scopeOf(btn));
        window.CplaQuote.add({
          handle: btn.dataset.handle,
          title: btn.dataset.title,
          url: btn.dataset.url,
          qty: qty,
          sizes: sizes,
          maxQty: qtyInput ? parseInt(qtyInput.max, 10) || null : null,
        });
        btn.classList.add('is-added');
        const label = btn.querySelector('[data-quote-add-label]');
        if (label) label.textContent = qty > 1 ? 'Added ×' + qty + ' ✓' : 'Added to quote ✓';
      });
    });
  };

  // --- PDP quantity stepper (capped by the product's Max quantity metafield) ---
  const clampQty = (input) => {
    const max = parseInt(input.max, 10) || 999;
    const value = Math.min(max, Math.max(1, parseInt(input.value, 10) || 1));
    input.value = value;
    // Tell the customer when they've asked for the whole inventory.
    const note = document.querySelector('[data-qty-max-note]');
    if (note) note.hidden = value < max;
    return value;
  };
  const bindQtySteppers = () => {
    document.querySelectorAll('[data-qty-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.closest('[data-quote-qty-wrap]')?.querySelector('[data-quote-qty]');
        if (!input) return;
        input.value = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
        clampQty(input);
      });
    });
    document.querySelectorAll('[data-quote-qty]').forEach((input) => {
      input.addEventListener('change', () => clampQty(input));
      input.addEventListener('blur', () => clampQty(input));
    });
  };

  // --- "Often rented with" pills: one-tap add, tap again to remove ---
  const paintQuickAdds = () => {
    const handles = window.CplaQuote.items().map((i) => i.handle);
    document.querySelectorAll('[data-quote-quickadd]').forEach((btn) => {
      const added = handles.includes(btn.dataset.handle);
      btn.classList.toggle('is-in-quote', added);
      btn.setAttribute('aria-label', (added ? 'Remove ' : 'Add ') + btn.dataset.title + (added ? ' from quote' : ' to quote'));
    });
  };
  const bindQuickAdds = () => {
    document.querySelectorAll('[data-quote-quickadd]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const inQuote = window.CplaQuote.items().some((i) => i.handle === btn.dataset.handle);
        if (inQuote) {
          window.CplaQuote.remove(btn.dataset.handle);
        } else {
          window.CplaQuote.add({
            handle: btn.dataset.handle,
            title: btn.dataset.title,
            url: btn.dataset.url,
          });
        }
      });
    });
  };

  // --- Quote page: render list + sync into the instruments textarea ---
  const AUTO_HEADER = 'Requested items:';
  const syncTextarea = () => {
    const field = document.getElementById('cf-instruments');
    if (!field) return;
    const items = window.CplaQuote.items();
    const line = (i) => {
      let s = '- ' + i.title + (i.qty > 1 ? ' × ' + i.qty : '');
      if (i.sizes && i.sizes.length) s += '\n    Sizes: ' + i.sizes.join(', ');
      return s;
    };
    const auto = items.length ? AUTO_HEADER + '\n' + items.map(line).join('\n') : '';
    // Preserve anything the customer typed after the auto block.
    const current = field.value;
    const idx = current.indexOf(AUTO_HEADER);
    let manual = current;
    if (idx !== -1) {
      // The auto block is the header, its "- item" lines, and any indented
      // continuation lines (sizes). It ends at the first line that is neither.
      const after = current.slice(idx).split('\n');
      let n = 0;
      while (n < after.length && (n === 0 || after[n].startsWith('-') || /^\s+\S/.test(after[n]))) n++;
      manual = (current.slice(0, idx) + after.slice(n).join('\n')).trim();
    }
    field.value = [auto, manual].filter(Boolean).join('\n\n');
  };

  const renderQuoteList = () => {
    const wrap = document.querySelector('[data-quote-list]');
    if (!wrap) return;
    const items = window.CplaQuote.items();
    wrap.hidden = items.length === 0;
    const empty = document.querySelector('[data-quote-empty]');
    if (empty) empty.hidden = items.length > 0;
    const ul = wrap.querySelector('ul');
    if (!ul) return;
    ul.innerHTML = '';
    items.forEach((i) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = i.url;
      a.textContent = i.title;

      const qty = Math.max(1, parseInt(i.qty, 10) || 1);
      const qtyWrap = document.createElement('span');
      qtyWrap.className = 'cpla-quote-qty font-mono';
      const dec = document.createElement('button');
      dec.type = 'button';
      dec.textContent = '−';
      dec.setAttribute('aria-label', 'Decrease quantity of ' + i.title);
      dec.addEventListener('click', () => {
        if (qty <= 1) return;
        window.CplaQuote.setQty(i.handle, qty - 1);
      });
      const n = document.createElement('span');
      n.textContent = '×' + qty;
      const inc = document.createElement('button');
      inc.type = 'button';
      inc.textContent = '+';
      inc.setAttribute('aria-label', 'Increase quantity of ' + i.title);
      inc.addEventListener('click', () => {
        window.CplaQuote.setQty(i.handle, qty + 1);
      });
      qtyWrap.appendChild(dec);
      qtyWrap.appendChild(n);
      qtyWrap.appendChild(inc);

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.setAttribute('aria-label', 'Remove ' + i.title);
      rm.textContent = '×';
      rm.addEventListener('click', () => {
        window.CplaQuote.remove(i.handle);
      });
      li.appendChild(a);
      if (i.sizes && i.sizes.length) {
        const s = document.createElement('span');
        s.className = 'cpla-quote-sizes';
        s.textContent = i.sizes.join(', ');
        li.appendChild(s);
      }
      li.appendChild(qtyWrap);
      li.appendChild(rm);
      ul.appendChild(li);
    });
    syncTextarea();
  };

  document.addEventListener('cpla:quote', () => {
    renderPills();
    renderQuoteList();
    paintQuickAdds();
  });

  document.addEventListener('DOMContentLoaded', () => {
    renderPills();
    bindSizePills();
    bindAddButtons();
    bindQtySteppers();
    bindQuickAdds();
    paintQuickAdds();
    renderQuoteList();
    // Successful submit → clear the list
    if (document.querySelector('[data-quote-submitted]')) {
      window.CplaQuote.clear();
    }
  });
})();
